import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { IconProtect } from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import {
  detailRendererContainerCss,
  PolicyManagementFormHeader
} from '../legacy-policy-panel/policy-management-form-header'
import {
  ControlsWithData,
  LegacyGrid,
  SortConfig
} from '../../legacy-design-system/legacy-grid'
import { CveShieldCell, DescriptionCell, ProtectModeCell } from './cell-content'
import { getCveShieldsRoute, updateRoute } from '../route'
import { CveShield } from '../protect-rules/shared'
import { hasRole, Role } from '../roles'
import { LegacyPolicyPanel } from '../legacy-policy-panel'
import {
  getRbacStatus,
  userHasResourceAction
} from 'contrastBase/accessControl'

export const gridDataTestID = 'cve-shields-defaults-grid'

const protectModeCellWidth = '130px'

// from _protection-policy.scss, line 111, tbody > tr > td
// added to every cell in order to match the styling from the legacy CVE Shields grid.
const bodyCellCss = {
  padding: '8px 15px',
  verticalAlign: 'middle'
}

// added to every column header in order to match the styling from the legacy CVE Shields grid.
const headingCss = {
  paddingLeft: '15px'
}

export interface CveShieldsDefaultsGridViewProps {
  readonly organizationUuid: string
  readonly organizationRole: Role
  readonly loading: boolean
  readonly rowData: CveShield[]
  readonly onSort: (sortConfig: SortConfig<CveShield>) => void
  readonly activeSortConfigs: Array<SortConfig<CveShield>>
  readonly onSearch: (value: string) => void
}

export function CveShieldsDefaultsGridView({
  organizationUuid,
  organizationRole: role,
  loading,
  rowData,
  onSort,
  activeSortConfigs,
  onSearch
}: CveShieldsDefaultsGridViewProps) {
  const intl = useIntl()
  const { isAssessEnforced } = getRbacStatus()
  const isModeUpdateDisabled = isAssessEnforced
    ? !userHasResourceAction('APPLICATION', 'PROTECT_POLICIES_MANAGE')
    : !hasRole(role, 'RULES_ADMIN')

  return (
    <LegacyPolicyPanel>
      <PolicyManagementFormHeader
        i18nID="title_cveShields_defaults"
        detailRenderer={() => (
          <div className={detailRendererContainerCss}>
            <Text font="body" color="neutral60">
              {intl.formatMessage({ id: 'detail_cveShields_defaults' })}
            </Text>
          </div>
        )}
        Icon={IconProtect}
        onClose={() => {
          updateRoute(getCveShieldsRoute(organizationUuid))
        }}
        marginBottom={0}
      />
      <ControlsWithData
        onSearch={onSearch}
        searchPlaceholder={{
          id: 'placeholder_findCveShield'
        }}
      />
      <LegacyGrid<CveShield>
        data-testid={gridDataTestID}
        rowClassName={css`
          vertical-align: top;
        `}
        onSort={onSort}
        loading={loading}
        rowData={rowData}
        emptyGridMessageConfig={{
          id: 'detail_noResultsFound'
        }}
        columnConfigs={[
          {
            styles: {
              width: '300px',
              ...bodyCellCss
            },
            heading: {
              id: 'title_cveShieldID'
            },
            headingStyles: headingCss,
            sorting: {
              field: 'name'
            },
            CellContent: ({ rowData: { name, cves } }) => (
              <CveShieldCell rowData={{ name, cves }} />
            )
          },
          {
            styles: {
              maxWidth: '300px',
              ...bodyCellCss
            },
            heading: {
              id: 'title_description'
            },
            headingStyles: headingCss,
            CellContent: ({ rowData: { description } }) => (
              <DescriptionCell rowData={{ description }} />
            )
          },
          {
            styles: {
              width: protectModeCellWidth,
              ...bodyCellCss
            },
            headingStyles: headingCss,
            horizontalCenter: true,
            heading: {
              id: 'title_serverEnvironmentDevelopment'
            },
            CellContent: ({
              rowData: {
                development: { protectMode },
                uuid
              }
            }) => (
              <ProtectModeCell
                rowData={{
                  protectMode,
                  uuid
                }}
                organizationUuid={organizationUuid}
                environment="DEVELOPMENT"
                disabled={isModeUpdateDisabled}
              />
            )
          },
          {
            styles: {
              width: protectModeCellWidth,
              ...bodyCellCss
            },
            horizontalCenter: true,
            heading: {
              id: 'title_serverEnvironmentQualityAssurance'
            },
            CellContent: ({
              rowData: {
                qa: { protectMode },
                uuid
              }
            }) => (
              <ProtectModeCell
                rowData={{
                  protectMode,
                  uuid
                }}
                organizationUuid={organizationUuid}
                environment="QA"
                disabled={isModeUpdateDisabled}
              />
            )
          },
          {
            styles: {
              width: protectModeCellWidth,
              ...bodyCellCss
            },
            horizontalCenter: true,
            heading: {
              id: 'title_serverEnvironmentProduction'
            },
            CellContent: ({
              rowData: {
                production: { protectMode },
                uuid
              }
            }) => (
              <ProtectModeCell
                rowData={{
                  protectMode,
                  uuid
                }}
                organizationUuid={organizationUuid}
                environment="PRODUCTION"
                disabled={isModeUpdateDisabled}
              />
            )
          }
        ]}
        activeSortConfigs={activeSortConfigs}
      />
    </LegacyPolicyPanel>
  )
}
